/**
 * The Conversations view of the Learning Catalog: the learning history. A per-cause strip (cases, trust, category) above a
 * filtered, server-paged table of every learned case, newest first. A row opens its details in a popup, where a case the
 * server allows it on can be undone (with a reason) or redone after a confirm that previews the effect on its cause. The data
 * comes from `useHistory` and the dropdown choices from `useCatalogOptions`, both owned by the page.
 */
import React, { useCallback, useState } from "react";
import { Loader2, WifiOff } from "lucide-react";

import CatalogPager from "./CatalogPager";
import DetailModal from "./DetailModal";
import HistoryCauseStrip from "./HistoryCauseStrip";
import HistoryDetail from "./HistoryDetail";
import HistoryFilters from "./HistoryFilters";
import HistoryRow from "./HistoryRow";
import { STATUS } from "./usePagedList";
import { EMPTY_HISTORY_FILTERS } from "./useHistory";
import { CATALOG_TEXT, HISTORY_ACTION, HISTORY_PAGE_SIZES, HISTORY_RESULT, HISTORY_TEXT } from "./catalogText";

const T = HISTORY_TEXT;
const COLUMNS = Object.freeze([T.table.when, T.table.category, T.table.cause, T.table.kind, T.table.status]);
const SECONDARY = "rounded-lg border border-[#EBECE6] bg-white px-3 py-1.5 text-sm font-semibold text-gray-600";

const noticeOf = (action, result, label) => {
  const done = result && result.result === HISTORY_RESULT.DONE;
  if (action === HISTORY_ACTION.UNDO) return done ? T.notices.undone(label) : T.notices.nothingToUndo;
  return done ? T.notices.redone(label) : T.notices.nothingToRedo;
};
const anyFilter = (filters) => Object.keys(EMPTY_HISTORY_FILTERS).some((k) => filters[k] !== EMPTY_HISTORY_FILTERS[k]);

/** `onChanged()` runs after an undo or redo, so the page can mark what else it shows as stale. */
export default function HistoryView({ history, options, products, onChanged }) {
  const { status, items, causes, total, all, error, filters, setFilter, clearFilters, offset, setOffset, limit, setLimit,
    refresh } = history;
  const [detail, setDetail] = useState(null);
  const [notice, setNotice] = useState("");

  const onFilter = (key, value) => { setNotice(""); setFilter(key, value); };
  const onClear = () => { setNotice(""); clearFilters(); };
  const onOpen = useCallback((item) => { setNotice(""); setDetail(item); }, []);
  const onDone = (action, result) => {
    setNotice(noticeOf(action, result, detail.cause_label));
    setDetail(null);
    refresh();
    if (onChanged) onChanged();
  };

  const ready = status === STATUS.READY;
  const loading = status === STATUS.LOADING;
  return (
    <div>
      <HistoryCauseStrip causes={causes} selected={filters.cause} onSelect={(cause) => onFilter("cause", cause)} />
      <HistoryFilters filters={filters} products={products} options={options} causes={causes} items={items}
        onChange={onFilter} onClear={onClear} />

      {notice && <p className="mb-3 text-sm text-emerald-700" role="status">{notice}</p>}
      {loading && items.length === 0 && (
        <p className="flex items-center gap-2 text-sm text-gray-500" role="status"><Loader2 size={14} className="animate-spin" />{T.loading}</p>
      )}
      {status === STATUS.NOT_CONNECTED && (
        <p className="flex items-center gap-2 text-sm text-gray-500" role="status"><WifiOff size={14} />{T.notConnected}</p>
      )}
      {status === STATUS.FAILED && <p className="text-sm text-red-600" role="alert">{error || T.failed}</p>}
      {ready && all === 0 && <p className="text-sm text-gray-500">{T.empty}</p>}
      {ready && all > 0 && total === 0 && (
        <div className="flex items-center gap-3 text-sm text-gray-500">
          <p>{T.noMatch}</p>
          <button type="button" className={SECONDARY} onClick={onClear}>{CATALOG_TEXT.filters.clear}</button>
        </div>
      )}

      {items.length > 0 && (ready || loading) && (
        <>
          <div className={`overflow-x-auto rounded-xl border border-[#EBECE6] bg-white shadow-sm transition-opacity ${loading ? "opacity-60" : ""}`}>
            <table className="w-full border-collapse text-sm tabular-nums" aria-busy={loading}>
              <caption className="sr-only">{T.table.caption}</caption>
              <thead>
                <tr className="bg-[#f6f6f1] text-left text-gray-600">
                  {COLUMNS.map((c) => <th key={c} scope="col" className="px-4 py-3 font-semibold whitespace-nowrap">{c}</th>)}
                </tr>
              </thead>
              <tbody>
                {items.map((item) => <HistoryRow key={item.eid} item={item} options={options} onOpen={onOpen} />)}
              </tbody>
            </table>
          </div>
          <CatalogPager offset={offset} limit={limit} total={total} shown={items.length} onOffset={setOffset}>
            {anyFilter(filters) && <span>{`(${T.paging.ofAll(all)})`}</span>}
            <select aria-label={T.paging.pageSize} value={limit} onChange={(e) => setLimit(Number(e.target.value))}
              className="rounded-lg border border-[#EBECE6] bg-white px-2 py-1.5 text-sm text-gray-600">
              {HISTORY_PAGE_SIZES.map((n) => <option key={n} value={n}>{T.paging.perPage(n)}</option>)}
            </select>
          </CatalogPager>
          <p className="mt-3 text-xs text-gray-500">{T.note}</p>
        </>
      )}

      {detail && (
        <DetailModal title={T.detail.title} onClose={() => setDetail(null)}>
          <HistoryDetail item={detail} options={options} causes={causes} onDone={onDone} />
        </DetailModal>
      )}
    </div>
  );
}
